/**
 * Typed wrappers around the panel REST API.
 *
 * Each exported function returns a Promise<T> using the central `api()` helper
 * so retries / JWT refresh / 401 redirects work uniformly.
 *
 * Where the backend's wire shape differs from the UI's preferred shape, we
 * keep the translation local to this file — the React pages then deal in a
 * single consistent vocabulary.
 */
import { api } from "./api";

/* ─────────────────────────── UI Types ─────────────────────────── */

export type JobKind = "account_full" | "account_db" | "account_meta" | "verify" | "prune";
export type JobState = "pending" | "running" | "done" | "failed" | "cancelled";

export type ServerRow = {
  id: string;
  name: string;
  host: string;
  port: number;
  agentVersion: string | null;
  online: boolean;
  lastSeenAt: string | null;
  diskTargetId: string | null;
  diskTarget: { id: string; name: string; mountPath: string } | null;
  accountCount: number;
  enabled: boolean;
  // Raw ServerState from the DB: distinguishes "paused" (drained — operator
  // soft-stop, no new jobs, in-flight finish) from "disabled" (hard-off) and
  // "unreachable" (heartbeat lost). The `enabled` boolean above collapses
  // these for the simple toggle; this field lets ops UI act on the nuance.
  state: "active" | "paused" | "disabled" | "unreachable";
  /**
   * Last result of the heartbeat probe ("ok" / "unreachable" / null
   * before the first cycle). Distinct from `state` — that's operator
   * intent, this is observed reality. UI can use it to surface the
   * "Resurrect Agent" affordance only when there's actually something
   * wedged to resurrect.
   */
  lastSyncStatus?: "ok" | "unreachable" | string | null;
  concurrencyMax: number;
  createdAt: string;
};

export type AccountRow = {
  id: string;
  user: string;
  primaryDomain: string | null;
  ownerEmail: string | null;
  serverId: string;
  serverName: string;
  groupId: string | null;
  groupName: string | null;
  commercial: boolean;
  suspended: boolean;
  graceUntil: string | null;
  sizeBytes: number | null;
  lastFullAt: string | null;
  lastDbAt: string | null;
  /** Total restic bytes from the most-recent successful account_full job. */
  lastFullBytes?: number | null;
  /**
   * Operator-set permanent exclusion. When true, scheduler skips this
   * account and the dispatcher rejects manual triggers with
   * `account excluded from backups`. Settable from the account detail
   * page → settings tab → "هرگز بکاپ نگیر".
   */
  excludeFromBackup?: boolean;
  /** True when the account was removed from cPanel but still has snapshots. */
  deleted?: boolean;
  deletedAt?: string | null;
  /**
   * Graveyard archives held for this account, newest first. Only the detail
   * endpoint fills this in; the list endpoint leaves it undefined.
   */
  archives?: GraveyardArchiveRow[];
};

/**
 * One archived copy of an account.
 *
 * `source` is the field that decides whether this may be handed to a customer:
 * `pkgacct` is a native cPanel backup, `restic` is a cpmove the panel
 * reconstructed from snapshots after the account had already been deleted.
 * They are equally valid as data and NOT equally valid as a product, which is
 * why the panel now says which one it is everywhere an archive appears.
 */
export type GraveyardArchiveRow = {
  id: string;
  state: string;
  source: "pkgacct" | "restic" | string;
  /**
   * WHY the copy exists, and therefore how long it is kept. `pre_deletion` is
   * the graveyard proper; `on_demand` is a transient delivery artifact that
   * lives only as long as its download link and is counted nowhere.
   */
  purpose: "pre_deletion" | "on_demand" | string;
  sizeBytes: number | null;
  takenAt: string;
  storedAt: string | null;
  expiresAt: string | null;
  errorMsg: string | null;
};

/** A graveyard row as the operator screen sees it: identity + delivery + why. */
export type GraveyardRow = GraveyardArchiveRow & {
  cpanelUser: string;
  whmcsServiceId: number | null;
  serverId: string;
  serverName: string;
  accountId: string | null;
  jobId: string | null;
  collectAttempts: number;
  createdAt: string;
  deliveryState: string | null;
  /** First 8 characters only — the token is the download credential. */
  deliveryToken: string | null;
  deliveryExpires: string | null;
  deliveryError: string | null;
};

/**
 * The capacity projection. `terminationsPerDay` is the number that decides
 * when hardware has to be ordered: `archivesPerDay` only measures how much of
 * that the capture pipeline is currently keeping up with.
 */
export type GraveyardProjection = {
  archivesPerDay: number;
  terminationsPerDay: number;
  avgBytes: number;
  retentionDays: number;
  requiredBytes: number;
  shortfallBytes: number;
  headroomDays: number | null;
};

export type GraveyardOverview = {
  capacity: {
    root: string;
    totalBytes: number;
    freeBytes: number;
    usedPct: number;
    minFreePct: number;
    acceptingNew: boolean;
    expiredBytes: number;
    expiredCount: number;
    /** On-demand copies on the same disk — reported beside the figures, never inside them. */
    transient: { count: number; bytes: number };
    projected: GraveyardProjection;
  };
  provenance: { source: string; state: string; count: number }[];
  funnel: {
    windowDays: number;
    terminated: number;
    requested: number;
    dispatched: number;
    stored: number;
    failed: number;
    pending: number;
    coveredTerminations: number;
  };
  /** On-demand copies in the same window — reported, never folded into the funnel. */
  transient: { windowDays: number; requested: number; stored: number };
  failures: { key: string; label: string; recoverable: boolean; count: number; sample: string | null }[];
  sweep: {
    lastRunAt: string | null;
    nextDueAt: string | null;
    last: {
      rescheduled?: number; purged?: number; freedBytes?: string;
      deliveriesExpired?: number; partsRemoved?: number;
      artifactsReleased?: number; orphansRemoved?: number; at?: number;
    } | null;
  };
  resticFallbackAllowed: boolean;
};

export type GraveyardCoverage = {
  query: string;
  covered: boolean;
  reason: string | null;
  archive: GraveyardRow | null;
  attempts: GraveyardRow[];
  account: { id: string; cpanelUser: string; serverName: string; deletedAt: string | null } | null;
};

/**
 * The graveyard: the last copy of an account, kept after it leaves the fleet.
 *
 * Everything here is read-mostly except the five actions at the bottom, each of
 * which touches a customer's only remaining copy of their site — so each is
 * audited server-side with the archive id.
 */
export const GraveyardApi = {
  overview: () => api<GraveyardOverview>("/ops/graveyard/overview"),

  archives: (params: {
    source?: string; state?: string; serverId?: string; q?: string;
    /** Omit for the graveyard proper; `all` also lists on-demand copies. */
    purpose?: "pre_deletion" | "on_demand" | "all";
    expiringDays?: number; sort?: string; limit?: number; offset?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "" && v !== null) qs.set(k, String(v));
    }
    return api<{ archives: GraveyardRow[]; total: number }>(`/ops/graveyard/archives?${qs.toString()}`);
  },

  coverage: (q: string) =>
    api<GraveyardCoverage>(`/ops/graveyard/coverage?q=${encodeURIComponent(q)}`),

  capture: (cpanelUser: string, serviceId?: number) =>
    api<{ ok: boolean; archiveId?: string; state?: string }>("/ops/graveyard/capture", {
      method: "POST",
      body: JSON.stringify({ cpanelUser, ...(serviceId ? { serviceId } : {}) }),
    }),

  deliver: (id: string, expiresInHours?: number) =>
    api<{ state: string; source: string; downloadUrl: string | null; expiresAt: string | null; error: string | null }>(
      `/ops/graveyard/archives/${encodeURIComponent(id)}/deliver`,
      { method: "POST", body: JSON.stringify(expiresInHours ? { expiresInHours } : {}) },
    ),

  revoke: (id: string) =>
    api<{ ok: boolean }>(`/ops/graveyard/archives/${encodeURIComponent(id)}/revoke`, { method: "POST" }),

  retry: (id: string) =>
    api<{ ok: boolean }>(`/ops/graveyard/archives/${encodeURIComponent(id)}/retry`, { method: "POST" }),

  purge: (id: string, why: string) =>
    api<{ ok: boolean; freedBytes?: number }>(`/ops/graveyard/archives/${encodeURIComponent(id)}/purge`, {
      method: "POST",
      body: JSON.stringify({ why }),
    }),

  sweep: () => api<{ ran: boolean }>("/ops/graveyard/sweep", { method: "POST" }),

  rescueDeleted: (dryRun: boolean, limit?: number) =>
    api<{ candidates: number; requeued: number; noSnapshots: number; alreadyArchived: number }>(
      "/ops/graveyard/rescue-deleted",
      { method: "POST", body: JSON.stringify({ dryRun, ...(limit ? { limit } : {}) }) },
    ),
};

export type GroupRow = {
  id: string;
  name: string;
  description: string | null;
  fullEveryDays: number;
  // false → no recurring DB-only backup; databases are captured only inside
  // the periodic full backup. dbEveryHours is then just the edit-form default.
  dbEnabled: boolean;
  dbEveryHours: number;
  keepFullDays: number;
  keepDbDays: number;
  paid: boolean;
  isDefault: boolean;
  accountCount: number;
  /**
   * ExcludeSet attached to this group's policies (account_full +
   * account_db are kept in sync). `null` means no user-defined set —
   * only the agent's built-in defaults apply.
   */
  excludeSetId?: string | null;
};

export type JobRow = {
  id: string;
  kind: JobKind;
  state: JobState;
  serverId: string | null;
  serverName: string | null;
  accountUser: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  progress: number;
  errorMessage: string | null;
  bytesWritten: number | null;
  duration: number | null;
  createdAt: string;
};

export type DashboardKpis = {
  serverCount: number;
  serverOnline: number;
  accountCount: number;
  runningJobs: number;
  errors24h: number;
  storageUsedBytes: number;
  storageFreeBytes: number;
  storageTotalBytes: number;
  avgDownloadMBps: number;
  avgPipelineMBps: number;
  avgWireMBps: number;
  storageActualBytes: number;
  storageLogicalBytes: number;
  dedupRatio: number;
  avgUptimeRatio: number;
  fullSnapshotCount: number;
  dbSnapshotCount: number;
  fullTotalBytes: number;
  dbTotalBytes: number;
  corruptedAccountCount: number;
  commercialCount: number;
};

export type DashboardResponse = {
  kpis: DashboardKpis;
  servers: ServerRow[];
  recentJobs: JobRow[];
};

export type Paginated<T> = { items: T[]; total: number };

/* ─────────────────────────── Wire ↔ UI Adapters ─────────────────────────── */

type WireServer = {
  id: string;
  name: string;
  hostname: string | null;
  sshHost: string;
  sshPort: number;
  state: "active" | "paused" | "disabled" | "unreachable";
  lastSeenAt: string | null;
  agentVersion: string | null;
  repoDisk: string | null;
  concurrencyMax?: number;
  createdAt: string;
};

function fromWireServer(s: WireServer & { accountCount?: number }): ServerRow {
  const online = s.state === "active" && !!s.lastSeenAt && Date.now() - new Date(s.lastSeenAt).getTime() < 5 * 60_000;
  return {
    id: s.id,
    name: s.name,
    host: s.sshHost,
    port: s.sshPort,
    agentVersion: s.agentVersion,
    online,
    lastSeenAt: s.lastSeenAt,
    diskTargetId: s.repoDisk,
    diskTarget: s.repoDisk ? { id: s.repoDisk, name: s.repoDisk, mountPath: `/mnt/${s.repoDisk}` } : null,
    accountCount: s.accountCount ?? 0,
    enabled: s.state !== "disabled",
    state: s.state,
    concurrencyMax: s.concurrencyMax ?? 0,
    createdAt: s.createdAt,
  };
}

type WireJob = {
  id: string;
  kind: string;
  state: string;
  serverName?: string | null;
  cpanelUser?: string | null;
  progressPct?: number | null;
  bytesTransferred?: string | number | null;
  queuedAt?: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  errorMsg?: string | null;
  serverId?: string;
};

function fromWireJob(j: WireJob): JobRow {
  const stateMap: Record<string, JobState> = {
    queued: "pending",
    running: "running",
    success: "done",
    partial_success: "done",
    failed: "failed",
    cancelled: "cancelled",
  };
  const startedAt = j.startedAt ?? null;
  const finishedAt = j.finishedAt ?? null;
  return {
    id: j.id,
    kind: (j.kind as JobKind) ?? "account_full",
    state: stateMap[j.state] ?? "pending",
    serverId: j.serverId ?? null,
    serverName: j.serverName ?? null,
    accountUser: j.cpanelUser ?? null,
    progress: Math.max(0, Math.min(100, Number(j.progressPct ?? 0))),
    bytesWritten: j.bytesTransferred != null ? Number(j.bytesTransferred) : null,
    errorMessage: j.errorMsg ?? null,
    startedAt,
    finishedAt,
    duration:
      startedAt && finishedAt
        ? Math.round((new Date(finishedAt).getTime() - new Date(startedAt).getTime()) / 1000)
        : null,
    createdAt: j.queuedAt ?? new Date().toISOString(),
  };
}

/* ─────────────────────────── Servers ─────────────────────────── */

export type ServerStats = {
  accounts: {
    total: number;
    suspended: number;
    /** Fresh full-backup coverage: lastFullAt within the freshness window (7d). */
    withFull: number;
    /** Stale-but-ever-backed-up: had a full backup at some point, but it expired. */
    withFullStale?: number;
    /** Never had a successful full backup at all. */
    withoutFull: number;
    withDb: number;
  };
  snapshots: {
    byKind: Record<string, { count: number; bytes: number; distinctAccounts: number }>;
    totalBytes: number;
    onDiskBytes: number | null;
    compressionRatio: number | null;
    repoPath: string | null;
    totalCount: number;
    lastBackupAt: string | null;
    distinctDatabases: number;
  };
  jobs: {
    byState: Record<string, number>;
    pending: number;
    running: number;
    queued: number;
    success: number;
    failed: number;
    last24h: number;
    last7d: number;
  };
  bulkProgress: BulkProgress;
};

export type BulkProgressByKind = {
  kind: string;
  total: number;
  done: number;
  failed: number;
  pending: number;
  queued: number;
  running: number;
  pct: number;
  triggeredBy: string;
};

export type BulkProgress = {
  active: boolean;
  kind: string | null;
  startedAt: string | null;
  total: number;
  done: number;
  failed: number;
  pending: number;
  queued: number;
  running: number;
  pct: number;
  /** Predominant trigger source for the session ("scheduler"|"manual"|"api"|"retry"|"cron"|"mixed"). */
  triggeredBy?: string;
  /** Per-kind breakdown when multiple kinds are active simultaneously. */
  byKind?: BulkProgressByKind[];
};

export type ProblemAccount = {
  accountId: string;
  cpanelUser: string;
  primaryDomain: string | null;
  kind: string;
  failCount: number;
  lastFailAt: string;
  lastErrorMsg: string | null;
  category: string;
  lastSuccessAt: string | null;
};

export type BulkProgressByServer = BulkProgress & {
  serverId: string;
  serverName: string;
};

export const ServersApi = {
  async list(): Promise<Paginated<ServerRow>> {
    const rows = await api<WireServer[]>("/servers");
    const mapped = rows.map((s) => fromWireServer(s));
    return { items: mapped, total: mapped.length };
  },
  async get(id: string): Promise<ServerRow> {
    const r = await api<WireServer>(`/servers/${id}`);
    return fromWireServer(r);
  },
  stats(id: string) {
    return api<ServerStats>(`/servers/${id}/stats`);
  },
  problemAccounts(id: string, opts: { hours?: number; minFails?: number; limit?: number } = {}) {
    const qs = new URLSearchParams();
    if (opts.hours != null) qs.set("hours", String(opts.hours));
    if (opts.minFails != null) qs.set("minFails", String(opts.minFails));
    if (opts.limit != null) qs.set("limit", String(opts.limit));
    return api<{ windowHours: number; items: ProblemAccount[] }>(
      `/servers/${id}/problem-accounts${qs.toString() ? "?" + qs.toString() : ""}`,
    );
  },
  async test(id: string) {
    return api<{ ok: boolean; ms: number; agentVersion?: string | null; error?: string }>(
      `/servers/${id}/test`,
      { method: "POST" }
    ).then((r) => ({
      ok: r.ok,
      agentVersion: r.agentVersion ?? undefined,
      latencyMs: r.ms,
      error: r.error,
    }));
  },
  async sync(id: string) {
    const r = await api<{ added?: number; updated?: number; removed?: number; total?: number }>(
      `/servers/${id}/sync`,
      { method: "POST" }
    );
    return { enqueued: (r.added ?? 0) + (r.updated ?? 0) };
  },
  /**
   * Trigger the panel's self-resurrect path for a stuck agent. Returns
   * the agent's reported systemctl status (typically "active" on
   * success). Safe to call against a healthy agent — it's a no-op.
   * Use when a server shows red unreachable status to skip the
   * heartbeat's 10-minute auto-resurrect cooldown.
   */
  async resurrect(id: string) {
    return api<{ ok: boolean; status: string; elapsedMs: number; error?: string }>(
      `/servers/${id}/resurrect`,
      { method: "POST" }
    );
  },
  patch(
    id: string,
    body: Partial<{
      enabled: boolean;
      diskTargetId: string | null;
      name: string;
      sshHost: string;
      sshPort: number;
      agentHost: string;
      agentPort: number;
      concurrencyMax: number;
      notes: string | null;
    }>,
  ) {
    const wire: Record<string, unknown> = {};
    if (body.enabled !== undefined) wire.state = body.enabled ? "active" : "paused";
    if (body.diskTargetId !== undefined) wire.repoDisk = body.diskTargetId;
    if (body.sshHost !== undefined) wire.sshHost = body.sshHost;
    if (body.sshPort !== undefined) wire.sshPort = body.sshPort;
    if (body.agentHost !== undefined) wire.agentHost = body.agentHost;
    if (body.agentPort !== undefined) wire.agentPort = body.agentPort;
    if (body.concurrencyMax !== undefined) wire.concurrencyMax = body.concurrencyMax;
    if (body.notes !== undefined) wire.notes = body.notes;
    return api<WireServer>(`/servers/${id}`, { method: "PATCH", body: JSON.stringify(wire) }).then(
      fromWireServer
    );
  },
  onboard(body: {
    name: string;
    sshHost: string;
    sshPort: number;
    rootPassword: string;
    agentPort?: number;
    concurrencyMax?: number;
    repoDisk?: string | null;
    notes?: string | null;
  }) {
    return api<OnboardResult>("/servers/onboard", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  /** Disks a repo may be assigned to. Reserved volumes are NOT in this list. */
  listDisks() {
    return api<{ disks: DiskInfo[]; reserved?: DiskInfo[] }>("/servers/disks").then((r) => r.disks);
  },
  /** Pool + reserved, for pickers that show reserved volumes as non-selectable. */
  listDisksWithReserved() {
    return api<{ disks: DiskInfo[]; reserved?: DiskInfo[] }>("/servers/disks").then((r) => ({
      disks: r.disks,
      reserved: r.reserved ?? [],
    }));
  },
  moveToDisk(serverId: string, toDisk: string, opts: { createIfMissing?: boolean } = {}) {
    return api<RelocateResult>(`/servers/${serverId}/disk`, {
      method: "POST",
      body: JSON.stringify({ toDisk, ...opts }),
    });
  },
  backfillDisks() {
    return api<{ updated: number; servers: Array<{ name: string; from: string | null; to: string }> }>(
      "/servers/disks/backfill",
      { method: "POST" }
    );
  },
};

export type DiskInfo = {
  id: string;
  mountPath: string;
  totalBytes: number;
  usedBytes: number;
  freeBytes: number;
  serverNames: string[];
  /** Dedicated to something other than the restic pool (graveyard archives). */
  reserved?: boolean;
  reservedFor?: string;
};
export type RelocateResult = {
  ok: boolean;
  serverName: string;
  fromDisk: string | null;
  toDisk: string;
  bytesMoved: number;
  ms: number;
  warnings: string[];
  error?: string;
};

export type OnboardStep = {
  step: string;
  ok: boolean;
  ms: number;
  detail?: string;
};
export type OnboardResult = {
  ok: boolean;
  serverId: string | null;
  serverName: string;
  steps: OnboardStep[];
  sync: { added: number; updated: number; removed: number; total: number } | null;
  errorMessage: string | null;
};

/* ─────────────────────────── Accounts ─────────────────────────── */

export const AccountsApi = {
  list(params: {
    q?: string;
    serverId?: string;
    groupId?: string;
    hasSnapshots?: boolean;
    snapshotKind?: "files" | "db" | "meta";
    /**
     * Restrict to accounts that have NEVER had a successful full
     * backup (lastFullAt IS NULL). Powers the "بدون بکاپ" status
     * filter on the accounts page.
     */
    noBackup?: boolean;
    /** Restrict to commercial ("ویژه") accounts (commercialFlag = true). */
    commercial?: boolean;
    /** Also include soft-deleted accounts (the restore picker uses this). */
    includeDeleted?: boolean;
    limit?: number;
    offset?: number;
  } = {}) {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.serverId) qs.set("serverId", params.serverId);
    if (params.groupId) qs.set("groupId", params.groupId);
    if (params.hasSnapshots) qs.set("hasSnapshots", "true");
    if (params.snapshotKind) qs.set("snapshotKind", params.snapshotKind);
    if (params.noBackup) qs.set("noBackup", "true");
    if (params.commercial) qs.set("commercial", "true");
    if (params.includeDeleted) qs.set("includeDeleted", "true");
    qs.set("limit", String(params.limit ?? 50));
    qs.set("offset", String(params.offset ?? 0));
    return api<Paginated<AccountRow>>(`/assignment/accounts?${qs.toString()}`);
  },
  history(id: string) {
    return api<JobRow[]>(`/assignment/accounts/${id}/history`);
  },
  get(id: string) {
    return api<AccountRow>(`/assignment/accounts/${encodeURIComponent(id)}`);
  },
  /**
   * Move an account into (or out of) a group. Passing `null` clears the
   * override and resets `assignmentSource = default_group` so the next
   * re-evaluate sweep picks the right group from AssignmentRules again.
   */
  moveGroup(accountId: string, groupId: string | null) {
    return api<{ id: string }>(`/accounts/${encodeURIComponent(accountId)}`, {
      method: "PATCH",
      body: JSON.stringify({ groupId }),
    });
  },
  /**
   * Generic per-account PATCH. Backend accepts a subset of writable
   * fields (`commercialFlag`, `notes`, `whmcsServiceId`,
   * `excludeFromBackup`, …). Use `moveGroup` for groupId changes
   * since that one mutates `assignmentSource` server-side.
   */
  patch(
    accountId: string,
    body: Partial<{
      commercialFlag: boolean;
      commercialExpiresAt: string | null;
      whmcsServiceId: string | null;
      notes: string | null;
      excludeFromBackup: boolean;
    }>,
  ) {
    return api<{ id: string }>(`/accounts/${encodeURIComponent(accountId)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  async backupNow(accountId: string, kind: "account_full" | "account_db" | "account_meta") {
    // Backend wants { serverId, cpanelUser, kind }. Resolve via the dedicated
    // GET-by-id endpoint — using `?q=<id>` was wrong because backend `q` only
    // searches cpanelUser/primaryDomain/ownerEmail, so the lookup was either
    // empty (causing "اکانت پیدا نشد" on every working account) or — in the
    // rare case the id collided with a username substring — silently picked
    // the wrong account and triggered a backup against the wrong tenant.
    const found = await api<AccountRow>(`/assignment/accounts/${encodeURIComponent(accountId)}`);
    const created = await api<{ jobId: string; agentJobId?: string; state?: string }>(
      `/jobs`,
      {
        method: "POST",
        body: JSON.stringify({ serverId: found.serverId, cpanelUser: found.user, kind }),
      }
    );
    return { jobId: created.jobId };
  },
  bulkBackup: (accountIds: string[], kind: "account_full" | "account_db" | "account_meta") =>
    api<{
      requested: number;
      queued: number;
      failed: number;
      items: Array<{ accountId: string; jobId: string }>;
      errors: Array<{ accountId: string; error: string }>;
    }>("/accounts/bulk-backup", {
      method: "POST",
      body: JSON.stringify({ accountIds, kind }),
    }),
};

/* ─────────────────────────── Groups ─────────────────────────── */

export const GroupsApi = {
  list: () => api<GroupRow[]>("/assignment/groups"),
  create: (g: Omit<GroupRow, "id" | "accountCount" | "isDefault">) =>
    api<GroupRow>(`/assignment/groups`, { method: "POST", body: JSON.stringify(g) }),
  update: (id: string, g: Partial<GroupRow>) =>
    api<GroupRow>(`/assignment/groups/${id}`, { method: "PATCH", body: JSON.stringify(g) }),
  remove: (id: string) => api<void>(`/assignment/groups/${id}`, { method: "DELETE" }),
  setDefault: (groupId: string) =>
    api<{ ok: true; defaultGroupId: string; name: string }>(
      `/assignment/groups/default`,
      { method: "PATCH", body: JSON.stringify({ groupId }) }
    ),
};

/* ─────────────────────────── Jobs ─────────────────────────── */

export const JobsApi = {
  async list(params: { state?: string; kind?: string; serverId?: string; limit?: number; offset?: number } = {}) {
    const qs = new URLSearchParams();
    // Translate UI states back to wire states for filtering
    if (params.state) {
      const m: Record<string, string> = { pending: "queued", done: "success" };
      qs.set("state", m[params.state] ?? params.state);
    }
    if (params.serverId) qs.set("serverId", params.serverId);
    if (params.kind) qs.set("kind", params.kind); // server-side kind filter
    qs.set("limit", String(params.limit ?? 50));
    qs.set("offset", String(params.offset ?? 0));
    const res = await api<{ total: number; items: WireJob[] }>(`/jobs?${qs.toString()}`);
    return { items: res.items.map(fromWireJob), total: res.total };
  },
  async get(id: string) {
    const j = await api<WireJob & { account?: { cpanelUser: string }; server?: { name: string } }>(`/jobs/${id}`);
    const flat: WireJob = {
      ...j,
      serverName: j.server?.name ?? j.serverName ?? null,
      cpanelUser: j.account?.cpanelUser ?? j.cpanelUser ?? null,
    };
    return fromWireJob(flat);
  },
  cancel: (id: string) => api<{ ok: boolean }>(`/jobs/${id}/cancel`, { method: "POST" }),
  /**
   * Bulk-retry every failed account_* job in a window. Returns counts;
   * the original failed rows are deleted server-side so the "recent
   * errors" UI clears on the next refetch.
   */
  retryBulk: (opts: { serverId?: string; hours?: number; limit?: number } = {}) =>
    api<{ matched: number; queued: number; skipped: number; deleted: number }>(
      `/jobs/retry-bulk`,
      { method: "POST", body: JSON.stringify(opts) },
    ),
  retry: (id: string) =>
    api<{ jobId: string; agentJobId: string; state: string }>(`/jobs/${id}/retry`, {
      method: "POST",
    }),
  streamUrl: (id: string) => `/api/v1/jobs/${encodeURIComponent(id)}/stream`,
};

/* ─────────────────────────── Dashboard ─────────────────────────── */

export type UploadHistoryItem = {
  day: string;
  fullBytes: number;
  dbBytes: number;
  metaBytes: number;
  totalBytes: number;
  jobs: number;
};

export type DashboardActivity = {
  id: string;
  at: string;
  actor: string;
  action: string;
  entityType: string;
  entityId: string | null;
  ip: string | null;
};

export type NotificationHealthEntry = {
  id: string;
  name: string;
  type: string;
  enabled: boolean;
  sent: number;
  failed: number;
  throttled: number;
};

/** Internet-quota snapshot from the TCI panel scraper. */
export type TciUsage = {
  fetchedAt: number | null;
  service: string | null;
  trafficRemainingGb: number;
  trafficTotalGb: number | null;
  trafficPercent: number | null;
  reserveGb: number | null;
  bundleGb: number | null;
  daysRemaining: number | null;
  daysTotal: number | null;
  serviceEnd: string | null;
  ageSec: number;
};

export const DashboardApi = {
  overview: () => api<DashboardResponse>("/dashboard/overview"),
  uploadHistory: (days = 30) =>
    api<{ days: number; items: UploadHistoryItem[] }>(
      `/dashboard/upload-history?days=${days}`,
    ),
  /** Returns the latest TCI scrape JSON. Throws 404 when stale/missing —
   * the caller should treat the error as "hide the card entirely". */
  tciUsage: () => api<TciUsage>("/dashboard/tci-usage"),
  /** Panel-host live metrics for the header chip. Throws 503 until the
   * background sampler has two snapshots; the header hides itself until
   * then. */
  /** Accounts with no usable copy of their SITE — see the at-risk endpoint. */
  atRisk: () =>
    api<{
      totals: {
        cannotRestore: number;
        neverBackedUp: number;
        lostFilesCopy: number;
        stale: number;
        inRecovery: number;
      };
      byServer: Array<{ name: string; count: number }>;
      samples: Array<{
        user: string;
        server: string;
        sizeBytes: number;
        lastFullAt: string | null;
      }>;
    }>("/dashboard/at-risk"),
  hostStats: () =>
    api<{
      cpuPercent: number | null;
      memPercent: number;
      memUsedBytes: number;
      memTotalBytes: number;
      load1: number | null;
      load5: number | null;
      load15: number | null;
      numCpus: number | null;
      ifaceName: string;
      rxBps: number | null;
      txBps: number | null;
      sampledAt: number;
    }>("/dashboard/host-stats"),
  diskHealth: () =>
    api<Array<{
      device: string;
      model: string;
      serial: string;
      type: "nvme" | "hdd";
      healthy: boolean | null;
      temperature: number | null;
      powerOnHours: number | null;
      reallocated: number | null;
      pendingSectors: number | null;
      availableSpare: number | null;
      percentUsed: number | null;
      mediaErrors: number | null;
      healthPercent: number | null;
    }>>("/dashboard/disk-health"),
  activity: (limit = 15) =>
    api<DashboardActivity[]>(`/dashboard/activity?limit=${limit}`),
  notificationHealth: (hours = 24) =>
    api<{ hours: number; items: NotificationHealthEntry[] }>(
      `/dashboard/notification-health?hours=${hours}`,
    ),
  bulkProgress: () =>
    api<{ items: BulkProgressByServer[] }>(`/dashboard/bulk-progress`),
  speedHistory: (hours = 24) =>
    api<{
      hours: number;
      dedupRatio: number;
      items: Array<{
        hour: string;
        pipelineMBps: number;
        wireMBps: number;
        jobs: number;
        bytes: number;
      }>;
    }>(`/dashboard/speed-history?hours=${hours}`),
  networkStatus: () =>
    api<{
      windowHours: number;
      dedupRatio: number;
      bytesReceived24h: number;
      bytesOnWire24h: number;
      peakPipelineMBps24h: number;
      peakWireMBps24h: number;
      activeTransferJobs: number;
    }>(`/dashboard/network-status`),
  /**
   * Recent failed jobs across the fleet (default 24h). When `serverId`
   * is set, restricts to that server only — used by the server detail
   * page's "خطاهای اخیر" panel.
   */
  recentErrors: (opts: { hours?: number; serverId?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.hours != null) qs.set("hours", String(opts.hours));
    if (opts.serverId) qs.set("serverId", opts.serverId);
    if (opts.limit != null) qs.set("limit", String(opts.limit));
    const tail = qs.toString();
    return api<RecentErrorsResponse>(
      `/dashboard/recent-errors${tail ? `?${tail}` : ""}`,
    );
  },
};

export type RecentErrorRow = {
  id: string;
  kind: string;
  state: string;
  serverId: string;
  serverName: string;
  accountUser: string | null;
  errorMessage: string | null;
  errorCode: string | null;
  queuedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
};

export type RecentErrorsResponse = {
  windowHours: number;
  total: number;
  items: RecentErrorRow[];
  byServer: { serverId: string; serverName: string; count: number }[];
};

/* ─────────────────────────── Restore ─────────────────────────── */

export type RestoreState = "pending" | "preparing" | "ready" | "failed" | "expired" | "revoked";
export type RestoreTargetType = "download_link" | "cpanel_homedir" | "cpanel_full";

export type RestoreRow = {
  id: string;
  accountId: string;
  accountUser?: string;
  serverName?: string;
  targetType: RestoreTargetType;
  state: RestoreState;
  downloadUrl: string | null;
  expiresAt: string | null;
  sizeBytes: number | null;
  errorMessage: string | null;
  createdAt: string;
  readyAt: string | null;
  failedAt: string | null;
  /** When the BACKUP was taken — distinct from `createdAt`, when the link was built. */
  snapshotAt?: string | null;
  snapshotKind?: "files" | "db" | "db_create" | "db_meta" | "meta" | "cpmove" | null;
  snapshotDbName?: string | null;
  /** What the artifact contained. Null for links built before this was recorded. */
  contents?: { files?: boolean; meta?: boolean; dbs?: string[] } | null;
};

export type SnapshotRow = {
  id: string;
  restic: string;
  kind: "files" | "db" | "db_meta" | "meta" | "cpmove";
  dbName: string | null;
  sizeBytes: number | null;
  createdAt: string;
  valid: boolean;
  tags: string[];
};

export type RecommendedSet = {
  files?: string;
  meta?: string;
  db_meta?: string;
  db?: Record<string, string>;
  db_create?: Record<string, string>;
};

export const RestoreApi = {
  list: (params: { accountId?: string; state?: RestoreState } = {}) => {
    const qs = new URLSearchParams();
    if (params.accountId) qs.set("accountId", params.accountId);
    if (params.state) qs.set("state", params.state);
    return api<RestoreRow[]>(`/restore?${qs.toString()}`);
  },
  get: (id: string) =>
    api<RestoreRow & {
      accessLogs: Array<{
        id: string;
        ip: string;
        userAgent: string | null;
        accessedAt: string;
        bytesServed: number | null;
        statusCode: number;
      }>;
    }>(`/restore/${id}`),
  create: (body: {
    accountId: string;
    snapshotIds: RecommendedSet;
    targetType: RestoreTargetType;
    targetServerId?: string;
    targetUser?: string;
    expiresInHours?: number;
  }) =>
    api<RestoreRow>(`/restore`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  revoke: (id: string) => api<{ ok: boolean }>(`/restore/${id}/revoke`, { method: "POST" }),
  retry: (id: string) => api<RestoreRow>(`/restore/${id}/retry`, { method: "POST" }),
  snapshots: (accountId: string) =>
    api<SnapshotRow[]>(`/restore/snapshots?accountId=${encodeURIComponent(accountId)}`),
  recommend: (accountId: string, asOf?: string) =>
    api<RecommendedSet>(
      `/restore/recommend/${encodeURIComponent(accountId)}` +
        (asOf ? `?asOf=${encodeURIComponent(asOf)}` : ""),
    ),
  /**
   * Bulk-create download_link restores for N accounts in one request.
   * Backend picks each account's latest valid snapshots automatically;
   * the result is per-row so a partial failure (e.g. one account that
   * has no valid snapshots) doesn't lose the URLs that did succeed.
   *
   * Currently UI-restricted to `download_link` — that's the only
   * target where bulk semantics make sense (a 50-account
   * cpanel_homedir bulk would write 50 different homedirs and is
   * better done one-by-one with explicit confirmation).
   */
  bulkCreate: (
    accountIds: string[],
    body: Partial<{ expiresInHours: number }> = {},
  ) =>
    api<BulkRestoreResponse>(`/restore/bulk`, {
      method: "POST",
      body: JSON.stringify({ accountIds, targetType: "download_link", ...body }),
    }),
};

export type BulkRestoreItem =
  | { accountId: string; ok: true; restoreId: string; cpanelUser: string; downloadUrl?: string | null }
  | { accountId: string; ok: false; cpanelUser?: string; error: string };

export type BulkRestoreResponse = {
  total: number;
  succeeded: number;
  failed: number;
  results: BulkRestoreItem[];
};

/* ─────────────────────────── Snapshots ─────────────────────────── */
//
// Backend route at /api/v1/snapshots — operator-facing read+verify surface
// described in plan §9.7. The UI dashboard exposes counts/sizes, but until
// this API+page existed there was no way to actually drill into the list
// and see which snapshots are valid, when they were verified, etc.

export type SnapshotKind = "files" | "db" | "db_create" | "db_meta" | "meta" | "cpmove";

export type SnapshotItem = {
  id: string;
  restic: string;
  kind: SnapshotKind;
  dbName: string | null;
  sizeBytes: number | null;
  serverId: string;
  serverName: string;
  accountId: string | null;
  accountUser: string | null;
  jobId: string;
  createdAt: string;
  valid: boolean;
  verifiedAt: string | null;
  verifyError: string | null;
  pruned: boolean;
  prunedAt: string | null;
};

export const SnapshotsApi = {
  list: (params: {
    serverId?: string;
    accountId?: string;
    kind?: SnapshotKind;
    valid?: boolean;
    prunedIncluded?: boolean;
    fromDate?: string;
    toDate?: string;
    limit?: number;
    offset?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.serverId) qs.set("serverId", params.serverId);
    if (params.accountId) qs.set("accountId", params.accountId);
    if (params.kind) qs.set("kind", params.kind);
    if (params.valid !== undefined) qs.set("valid", String(params.valid));
    if (params.prunedIncluded) qs.set("prunedIncluded", "true");
    if (params.fromDate) qs.set("fromDate", params.fromDate);
    if (params.toDate) qs.set("toDate", params.toDate);
    qs.set("limit", String(params.limit ?? 100));
    qs.set("offset", String(params.offset ?? 0));
    return api<{ total: number; items: SnapshotItem[] }>(`/snapshots?${qs.toString()}`);
  },
  verify: (id: string) =>
    api<{ jobId: string; verified: number; missing: number; errors: string[] }>(
      `/snapshots/${id}/verify`,
      { method: "POST" },
    ),
  ls: (id: string, params: { path?: string; recursive?: boolean; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.path) qs.set("path", params.path);
    if (params.recursive) qs.set("recursive", "1");
    if (params.limit) qs.set("limit", String(params.limit));
    const q = qs.toString();
    return api<{
      snapshot: string;
      path: string;
      count: number;
      truncated: boolean;
      entries: Array<{
        type: string;
        name?: string;
        path?: string;
        size?: number;
        mtime?: string;
      }>;
    }>(`/snapshots/${id}/ls${q ? `?${q}` : ""}`);
  },
};

/* ─────────────────────────── Notifications ─────────────────────────── */

export type NotifChannelKind = "telegram" | "webhook" | "smtp" | "sms";

export type NotifChannel = {
  id: string;
  name: string;
  kind: NotifChannelKind;
  enabled: boolean;
  minLevel: "info" | "warn" | "error";
  lastSentAt: string | null;
  createdAt: string;
};

export type NotifLogEntry = {
  id: string;
  sentAt: string;
  channelId: string;
  channelName: string;
  channelType: string;
  event: string;
  status: "sent" | "failed" | "throttled";
  errorMsg: string | null;
  payload: {
    level: "info" | "warn" | "error" | null;
    title: string | null;
    message: string | null;
  };
};

export const NotifApi = {
  list: () => api<NotifChannel[]>("/notifications/channels"),
  create: (body: { name: string; kind: NotifChannelKind; minLevel: string; config: Record<string, unknown> }) =>
    api<NotifChannel>("/notifications/channels", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: Partial<{ name: string; enabled: boolean; minLevel: string; config: Record<string, unknown> }>) =>
    api<NotifChannel>(`/notifications/channels/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: string) => api<void>(`/notifications/channels/${id}`, { method: "DELETE" }),
  test: (id: string) => api<{ ok: boolean }>(`/notifications/channels/${id}/test`, { method: "POST" }),
  log: (params: {
    channelId?: string;
    status?: "sent" | "failed" | "throttled";
    event?: string;
    limit?: number;
    offset?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.channelId) qs.set("channelId", params.channelId);
    if (params.status) qs.set("status", params.status);
    if (params.event) qs.set("event", params.event);
    qs.set("limit", String(params.limit ?? 100));
    qs.set("offset", String(params.offset ?? 0));
    return api<{ total: number; items: NotifLogEntry[] }>(`/notifications/log?${qs.toString()}`);
  },
};

/* ─────────────────────────── Users (admin) ─────────────────────────── */

export type UserRole = "superadmin" | "admin" | "operator" | "viewer" | "support";

export type UserRow = {
  id: string;
  username: string;
  email: string;
  fullName: string;
  role: UserRole;
  enabled: boolean;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  totpEnabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export const UsersApi = {
  list: () => api<UserRow[]>("/users"),
  /**
   * Create a panel user. `email` and `role` are optional — the
   * settings UI no longer asks for either:
   *   - role defaults to "superadmin" on the backend (the only role
   *     this install uses);
   *   - email defaults to "<username>@local", a placeholder kept only
   *     so the DB column's NOT NULL + UNIQUE constraints survive
   *     without a migration.
   * Callers can still pass them when scripting against the API.
   */
  create: (body: {
    username: string;
    fullName: string;
    password: string;
    email?: string;
    role?: UserRole;
  }) => api<UserRow>("/users", { method: "POST", body: JSON.stringify(body) }),
  update: (
    id: string,
    body: Partial<{ email: string; fullName: string; role: UserRole; enabled: boolean }>,
  ) =>
    api<UserRow>(`/users/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  resetPassword: (id: string, password: string) =>
    api<{ ok: boolean }>(`/users/${id}/password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  resetTotp: (id: string) =>
    api<{ ok: boolean }>(`/users/${id}/totp/reset`, { method: "POST" }),
  remove: (id: string) => api<void>(`/users/${id}`, { method: "DELETE" }),
};

/* ─────────────────────────── TOTP (self-service) ─────────────────────────── */

export const TotpApi = {
  setup: () => api<{ otpauth: string; secret: string }>("/auth/totp/setup", { method: "POST" }),
  enable: (code: string) =>
    api<{ ok: boolean }>("/auth/totp/enable", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  disable: (password: string) =>
    api<{ ok: boolean }>("/auth/totp/disable", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
};

/* ─────────────────────────── Settings ─────────────────────────── */

export const SettingsApi = {
  all: () => api<Record<string, unknown>>("/settings"),
  set: (kv: Record<string, unknown>) =>
    api<{ ok: boolean }>("/settings", { method: "PATCH", body: JSON.stringify(kv) }),
};

/* ─────────────────────────── Audit Log ─────────────────────────── */

export type AuditEntry = {
  id: string;
  createdAt: string;
  userEmail: string | null;
  apiKeyName: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  ip: string | null;
  userAgent: string | null;
  // Structured payload describing what changed. Shape varies by action —
  // settings.update: { keys: [...] }, ops.server.wipe: { name, snapshotsPruned },
  // etc. UI renders as pretty JSON on row expand.
  diff: unknown;
};

export const AuditApi = {
  list: (
    params: {
      q?: string;
      action?: string;
      entityType?: string;
      limit?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.action) qs.set("action", params.action);
    if (params.entityType) qs.set("entityType", params.entityType);
    qs.set("limit", String(params.limit ?? 100));
    return api<AuditEntry[]>(`/audit?${qs.toString()}`);
  },
  facets: () =>
    api<{ actions: string[]; entityTypes: string[] }>("/audit/facets"),
};

/* ─────────────────────────── Ops / Scheduler ─────────────────────────── */

export const OpsApi = {
  runScheduler: () => api<{ enqueued?: number; skipped?: number }>("/scheduler/tick", { method: "POST" }).then(
    (r) => ({ enqueued: r.enqueued ?? 0, skipped: r.skipped ?? 0 })
  ),
  schedulerStatus: () =>
    api<{ killSwitch: boolean; globalConcurrencyMax: number }>("/scheduler/status").then((r) => ({
      running: !r.killSwitch,
      lastRunAt: null as string | null,
      killswitch: r.killSwitch,
    })),
  setKillswitch: (on: boolean) =>
    api<{ ok: boolean; killSwitch: boolean }>("/scheduler/kill", {
      method: "POST",
      body: JSON.stringify({ enable: on }),
    }).then((r) => ({ killswitch: r.killSwitch })),
  runVerify: () =>
    api<{ verified?: number; failed?: number }>("/ops/verify", { method: "POST" }).then((r) => ({
      jobId: `verify-sweep (${r.verified ?? 0} ok / ${r.failed ?? 0} bad)`,
    })),
  runPruneDry: () =>
    api<{ removed?: number; freedBytes?: number; wouldRemove?: number; bytes?: number }>(
      "/ops/prune/dry",
      { method: "POST" }
    ).then((r) => ({
      wouldRemove: r.wouldRemove ?? r.removed ?? 0,
      bytes: r.bytes ?? r.freedBytes ?? 0,
    })),
  runPrune: () =>
    api<{ removed?: number; freedBytes?: number; bytes?: number }>("/ops/prune", { method: "POST" }).then(
      (r) => ({ removed: r.removed ?? 0, bytes: r.bytes ?? r.freedBytes ?? 0 })
    ),
  runOrphan: () =>
    api<{ candidates: number; forgotten: number; pruned: number; archived: number; errors: string[] }>(
      "/ops/orphan",
      { method: "POST" },
    ),
  runOrphanDry: () =>
    api<{ candidates: number; retentionDays: number }>("/ops/orphan/dry", { method: "POST" }),
  orphanStats: (previewDays?: number) => {
    const qs = previewDays && previewDays > 0 ? `?days=${previewDays}` : "";
    return api<{
      configuredDays: number;
      effectiveDays: number;
      defaultDays: number;
      pendingNow: number;
      pendingNext24h: number;
      pendingTotal: number;
      samples: Array<{
        id: string;
        cpanelUser: string;
        serverName: string;
        deletedAt: string | null;
      }>;
    }>(`/ops/orphan/stats${qs}`);
  },
  /**
   * Archive-volume usage AND the steady-state projection. The projection is the
   * number that matters: a volume looks empty for exactly as long as the
   * capture pipeline is failing.
   */
  graveyardCapacity: () =>
    api<{
      root: string;
      totalBytes: number;
      freeBytes: number;
      usedPct: number;
      minFreePct: number;
      acceptingNew: boolean;
      expiredBytes: number;
      expiredCount: number;
      transient: { count: number; bytes: number };
      projected: GraveyardProjection;
      totalTb: number;
      freeTb: number;
      requiredTb: number;
      shortfallTb: number;
      headroomDays: number | null;
      retentionSustainable: boolean;
    }>("/ops/graveyard/capacity"),
  drain: (serverId: string) =>
    api<{ ok: boolean; server: string; state: string }>(`/ops/drain/${serverId}`, { method: "POST" }),
  undrain: (serverId: string) =>
    api<{ ok: boolean; server: string; state: string }>(`/ops/undrain/${serverId}`, { method: "POST" }),
  wipeServer: (serverId: string, confirm: string) =>
    api<{ ok: boolean; server: string; snapshotsPruned: number }>(
      `/ops/wipe-server/${serverId}`,
      { method: "POST", body: JSON.stringify({ confirm }) },
    ),
  bulkStop: (input: {
    serverId?: string;
    kind?: "account_full" | "account_db" | "account_meta";
  } = {}) =>
    api<{ ok: boolean; cancelledPending: number }>(`/ops/bulk-stop`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  bulkBackup: (input: {
    serverId?: string;
    groupId?: string;
    kind?: "account_full" | "account_db" | "account_meta";
    limit?: number;
  }) =>
    api<{ ok: boolean; considered: number; queued: number; skipped: number; errors: string[] }>(
      "/ops/bulk-backup",
      { method: "POST", body: JSON.stringify(input) },
    ),

  // Bulk seed — initial-bootstrap orchestrator for the whole fleet
  // (plan §8 + §15 phase 4). start/stop are POST with empty body; status
  // returns the persistent progress object kept in Setting("seed.progress").
  seedStart: () =>
    api<{ started: boolean; reason?: string }>("/ops/seed/start", { method: "POST" }),
  seedStop: () => api<{ ok: boolean }>("/ops/seed/stop", { method: "POST" }),
  seedStatus: () =>
    api<{
      running: boolean;
      startedAt: string | null;
      totalAccounts: number;
      submittedAccountIds: string[];
      skippedAccountIds: string[];
      lastError: string | null;
      perServer: Record<string, { submitted: number; skipped: number }>;
    }>("/ops/seed/status"),

  // Migrate server's restic repo from one disk to another (plan §9.9).
  // Long-running and synchronous — UI should show "this will take 10-30 min".
  migrateDisk: (serverId: string, toDisk: string) =>
    api<{
      ok: boolean;
      serverName: string;
      fromDisk: string | null;
      toDisk: string;
      bytesMoved: number;
      ms: number;
      warnings: string[];
      error?: string;
    }>("/ops/migrate-disk", {
      method: "POST",
      body: JSON.stringify({ serverId, toDisk }),
    }),

  // List physical disks (mount + capacity + repos on each), used by the
  // migrate-disk wizard and the storage page.
  listDisks: () =>
    api<
      Array<{
        id: string;
        mountPath: string;
        sizeBytes: number;
        usedBytes: number;
        freeBytes: number;
        repos: Array<{ serverName: string; sizeBytes: number }>;
      }>
    >("/ops/disks"),
};

/* ─────────────────────────── Assignment extras ─────────────────────────── */

export type AssignmentRuleSource =
  | "cpanel_package"
  | "username_pattern"
  | "domain_suffix"
  | "email_pattern"
  | "size_range_gb"
  | "manual_list"
  | "commercial_flag"
  | "server_id";

export type AssignmentRule = {
  id: string;
  groupId: string;
  groupName?: string;
  priority: number;
  source: AssignmentRuleSource;
  matchValue: unknown;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export const AssignmentApi = {
  // Plan §9.3 — "این قانون روی X اکانت match می‌کنه". Pass a rule body
  // (NOT persisted) and get the count + sample of matching accounts.
  previewRule: (body: {
    source: AssignmentRuleSource;
    matchValue: unknown;
    sampleSize?: number;
  }) =>
    api<{
      count: number;
      sample: Array<{ id: string; cpanelUser: string; primaryDomain: string | null; serverName: string }>;
      error?: string;
    }>("/assignment/rules/preview", { method: "POST", body: JSON.stringify(body) }),

  listRules: (groupId?: string) => {
    const qs = groupId ? `?groupId=${encodeURIComponent(groupId)}` : "";
    return api<AssignmentRule[]>(`/assignment/rules${qs}`);
  },

  // Whole-server → group mapping (managed via a single server_id rule each).
  listServerGroups: () =>
    api<{ items: Array<{ serverId: string; groupId: string; groupName: string; ruleId: string; enabled: boolean }> }>(
      "/assignment/server-groups",
    ),
  // Assign every account on a server to a group (groupId=null clears it).
  // Returns how many accounts changed group as a result of the re-evaluation.
  setServerGroup: (serverId: string, groupId: string | null) =>
    api<{ ok: boolean; serverId: string; groupId: string | null; updated: number; total: number }>(
      "/assignment/server-group",
      { method: "PUT", body: JSON.stringify({ serverId, groupId }) },
    ),
  createRule: (body: {
    groupId: string;
    priority: number;
    source: AssignmentRuleSource;
    matchValue: unknown;
    enabled?: boolean;
  }) =>
    api<AssignmentRule>("/assignment/rules", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateRule: (
    id: string,
    body: Partial<{
      groupId: string;
      priority: number;
      source: AssignmentRuleSource;
      matchValue: unknown;
      enabled: boolean;
    }>,
  ) =>
    api<AssignmentRule>(`/assignment/rules/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  removeRule: (id: string) =>
    api<void>(`/assignment/rules/${id}`, { method: "DELETE" }),
  reorderRules: (ids: string[]) =>
    api<{ ok: boolean }>("/assignment/rules/reorder", {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),

  // Plan §9.3 Simulate tab — forecast a group's load over 24h / 7d based on
  // active policies + recent snapshot sizes.
  simulateGroup: (groupId: string) =>
    api<{
      groupId: string;
      groupName: string;
      memberCount: number;
      policies: Array<{
        policyId: string;
        name: string;
        kind: string;
        cadenceHours: number;
        jobs24h: number;
        jobs7d: number;
        medianBytesPerJob: number;
        bytes24h: number;
        bytes7d: number;
      }>;
      totals: { jobs24h: number; jobs7d: number; bytes24h: number; bytes7d: number };
      note: string;
    }>(`/assignment/groups/${groupId}/simulate`),
};

/* ─────────────────────────── Excludes API ─────────────────────────── */

export type ExcludeSetRow = {
  id: string;
  name: string;
  description: string | null;
  patterns: string[];
  isSystem: boolean;
  policyCount: number;
  createdAt: string;
};

export const ExcludesApi = {
  list: () => api<ExcludeSetRow[]>("/excludes"),
  get: (id: string) => api<ExcludeSetRow>(`/excludes/${id}`),
  create: (input: { name: string; description?: string | null; patterns: string[] }) =>
    api<ExcludeSetRow>("/excludes", { method: "POST", body: JSON.stringify(input) }),
  update: (id: string, input: Partial<{ name: string; description: string | null; patterns: string[] }>) =>
    api<ExcludeSetRow>(`/excludes/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  remove: (id: string) => api<void>(`/excludes/${id}`, { method: "DELETE" }),
  preview: (input: { accountId: string; patterns: string[]; sampleLimit?: number }) =>
    api<{ ok: boolean; matches: string[]; truncated: boolean; message?: string }>(
      "/excludes/preview",
      { method: "POST", body: JSON.stringify(input) },
    ),
};

/* ─────────────────────────── Storage API ─────────────────────────── */

export type StorageDisk = {
  id: string;
  mountPath: string;
  totalBytes: number;
  usedBytes: number;
  freeBytes: number;
  usedPct: number;
  servers: string[];
};

export type StorageHealth = {
  disks: StorageDisk[];
  totals: { totalBytes: number; usedBytes: number; freeBytes: number };
  thresholds: { warnPct: number; critPct: number };
};

export type StorageForecastEntry = {
  diskId: string;
  usedBytes: number;
  freeBytes: number;
  totalBytes: number;
  slopeBytesPerDay: number;
  rSquared: number;
  daysUntilFull: number | null;
  confidence: "high" | "medium" | "low";
};

export type StorageGrowthItem = {
  day: string;
  perDisk: Record<string, number>;
  total: number;
};

export const StorageApi = {
  health: () => api<StorageHealth>("/storage/health"),
  forecast: () =>
    api<{ windowDays: number; disks: StorageForecastEntry[] }>("/storage/forecast"),
  byServer: () =>
    api<Array<{ server: string; disk: string; mountPath: string }>>("/storage/by-server"),
  growth: (days = 30) =>
    api<{ days: number; diskIds: string[]; items: StorageGrowthItem[] }>(
      `/storage/growth?days=${days}`,
    ),
};
